/**
 * Module-level coordination state for page_render tracking.
 *
 * When a page calls useRenderTracker({ ready }), it "claims" the current
 * pathname, signaling the auto-tracker (RenderTracker) to skip its default
 * fire-on-mount behavior for that pathname.
 *
 * This works because React fires effects bottom-up in render order:
 * page effects (inside Outlet) run before RenderTracker's effects
 * (sibling after Outlet in Root.tsx).
 *
 * NOTE: Intentionally a single-value claim (not a Set). Only one page
 * component should claim at a time — the leaf route that owns the
 * async data gate. Nested layouts should NOT claim independently.
 */

let claimedPathname: string | null = null

/**
 * Claim a pathname for manual page_render tracking.
 * Called by useRenderTracker when the `ready` parameter is provided.
 */
export function claimPathname(pathname: string): void {
  claimedPathname = pathname
}

/**
 * Check if a pathname has been claimed by a manual useRenderTracker call.
 * Called by RenderTracker before firing the default page_render event.
 */
export function isPathnameClaimed(pathname: string): boolean {
  return claimedPathname === pathname
}

/**
 * Release the claim for a pathname.
 * Called during cleanup when a page with useRenderTracker unmounts.
 * Only clears if the current claim matches, preventing race conditions
 * where a new page claims before the old page's cleanup runs.
 */
export function releasePathname(pathname: string): void {
  if (claimedPathname === pathname) {
    claimedPathname = null
  }
}
